
export enum AppTab {
  PATIENTS = 'patients',
  AGENDA = 'agenda',
  REPORT_ANALYSIS = 'report_analysis',
  DECISION = 'decision',
  CONSULT = 'consult',
  ATLAS = 'atlas',
  BUREAUCRACY = 'bureaucracy',
  CLINICAL_REGISTRY = 'clinical_registry',
  SUPPORT_CENTER = 'support_center'
}

export type Language = 'pt' | 'en' | 'es';

export interface Classification {
  id: string;
  name: string;
  category: string;
  criteria: {
    label: string;
    options: string[];
  }[];
  getResult: (selections: string[]) => {
    title: string;
    description: string;
    action: string;
    antibiotics?: string;
    antibioticDetails?: { // New: For Calculator
      drug: string;
      doseMgKg: number; // e.g. 30mg/kg
      frequencyHours: number; // e.g. 6 (every 6h)
      maxDoseMg?: number;
    };
    urgency: 'Low' | 'Medium' | 'High' | 'Immediate';
    references?: string[]; // New: Academic References
    materials?: string[]; // New: Link to Inventory
    checklist?: string[]; // New: Safety Checklist
  };
}

export interface SurgicalTemplate {
  id: string;
  title: string;
  description: string;
  defaultText: string;
}

export interface InventoryItem {
  id: string;
  name: string;
  availability: 'Available' | 'Low' | 'Out of Stock';
  stock: number;
  tags?: string[]; // Added for smart suggestions
}

export interface Contact {
  id: string;
  name: string;
  phone: string;
}

export interface ClinicalNote {
  id: string;
  date: number;
  type: 'Protocol' | 'Consult' | 'Document' | 'Atlas' | 'Consent';
  title: string;
  content: string;
  titleKey?: string;
  contentKey?: string;
}

export interface Doctor {
  name: string;
  crm: string;
  crmState?: string;
  rqe?: string;
  specialty?: string;
  subspecialty?: string;
  orthoId?: string;
  uid?: string;
  email?: string;
  hospital?: string;
  phone?: string;
}

export interface PatientFile {
  id: string;
  name: string;
  fileName?: string;
  url: string;
  storagePath?: string;
  path?: string;
  contentType: string;
  mimeType?: string;
  kind?: 'image' | 'pdf' | 'dicom' | 'other';
  type?: string;
  source?: 'web' | 'mobile';
  uploadedBy?: string;
  size?: number;
  createdAt: string; // ISO string
}

export interface ClinicalRegistry {
  diagnosis: {
    bone: string;
    classificationSystem: string;
    classificationCode: string;
    injuryMechanism: string;
  };
  treatment: {
    strategy: string;
    implantType: string;
    fixationType: string;
  };
  procedure: {
    procedureType: string;
    procedureDate: string;
  };
  outcome: {
    healingStatus: string;
    complications: string;
    followUpMonths: number;
  };
}

export interface CaseFingerprint {
  bone: string;
  classification: string;
  treatmentStrategy: string;
  fixationType: string;
}

export const createInitialClinicalRegistry = (): ClinicalRegistry => ({
  diagnosis: {
    bone: "",
    classificationSystem: "",
    classificationCode: "",
    injuryMechanism: "",
  },
  treatment: {
    strategy: "",
    implantType: "",
    fixationType: "",
  },
  procedure: {
    procedureType: "",
    procedureDate: "",
  },
  outcome: {
    healingStatus: "",
    complications: "",
    followUpMonths: 0,
  },
});

export const createCaseFingerprintFromRegistry = (
  clinicalRegistry?: ClinicalRegistry,
  existing?: CaseFingerprint
): CaseFingerprint | undefined => {
  if (!clinicalRegistry && !existing) return undefined;

  return {
    bone: clinicalRegistry?.diagnosis.bone || existing?.bone || "",
    classification:
      clinicalRegistry?.diagnosis.classificationCode || existing?.classification || "",
    treatmentStrategy:
      clinicalRegistry?.treatment.strategy || existing?.treatmentStrategy || "",
    fixationType:
      clinicalRegistry?.treatment.fixationType || existing?.fixationType || "",
  };
};

export type DataSharingMode = 'private' | 'anonymous_registry' | 'research';

export interface Patient {
  id: string;
  name: string;
  age: string;
  sex: 'M' | 'F';
  side: 'Left' | 'Right' | 'Bilateral';
  anatomy: string;
  comorbidities: string[];

  // ✅ mantenha se você ainda usa para preview/local (mas NÃO salve base64 no Firestore)
  // Legacy fallback for older records. New uploads should go to patientFiles.
  images?: string[];

  // ✅ novo: arquivos reais do paciente no Storage
  patientFiles?: PatientFile[];

  status: 'Pre-op' | 'Post-op' | 'Urgency';
  createdAt: number;

  // (mantenha os demais campos que você já tem abaixo)
  
  // New Fields
  cpf?: string;
  phone?: string;
  email?: string;
  weight?: string; // in kg
  height?: string; // in meters (e.g. 1.75)
  bmi?: string;    // Calculated
  allergies?: string[]; // New: Allergy list
  chiefComplaint?: string;
  historyOfPresentIllness?: string;
  painLocation?: string;
  symptomDuration?: string;
  traumaMechanism?: string;
  functionalLimitation?: string;
  physicalExam?: string;
  profession?: string;
  suspectedDiagnosis?: string;
  initialPlan?: string;
  notes?: string;

  // Optional linkage to generated data
  diagnosis?: string;
  surgicalPlan?: string;
  clinicalRegistry?: ClinicalRegistry;
  caseFingerprint?: CaseFingerprint;
  dataSharingMode?: DataSharingMode;
  clinicalNotes: ClinicalNote[];
  doctorOrthoId?: string;
  responsibleDoctorUid?: string;
  responsibleDoctorName?: string;
  responsibleDoctorEmail?: string;
  doctor?: Doctor;
  responsibleDoctor?: {
    name: string;
    crm: string;
    crmState?: string;
    rqe?: string;
    specialty?: string;
    subspecialty?: string;
    orthoId?: string;
  };
}

// --- NEW TYPES FOR HYBRID DATABASE ---

export interface UserNote {
  id: string;
  pathologyId: string;
  content: string; // The text note or caption
  type: 'text' | 'audio' | 'photo';
  tags: string[]; // Smart Tags
  timestamp: number;
  attachmentUrl?: string; // Mock URL for images/audio
}

export interface Taxonomy {
  anatomy: string[]; // e.g., 'Knee', 'Shoulder'
  pathology: string[]; // e.g., 'Trauma', 'Tumor'
  classification: string[]; // e.g., 'AO/OTA', 'Schatzker'
}

export interface LocalPathology {
  id: string;
  nome: string;
  cid10?: string;
  taxonomy: Taxonomy;
  synonyms?: string[]; // Added for smart search
  
  // Tab 1: General
  resumo: string;
  imagemReferencia?: string; // URL or Base64
  classificacaoDetalhada: {
    nome: string;
    tipos: { nome: string; descricao: string; imagem?: string }[];
  }[];

  // Tab 2: Treatment
  condutaConservadora?: string;
  condutaCirurgica?: string;
  checklistMaterial: string[]; // Pre-op items
  
  // Tab 3 is Dynamic (User Notes)
  
  isUserAdded?: boolean;
}
